import React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { XIcon, InfoIcon } from 'lucide-react';
import { UCUM_CATEGORIES } from '../../constants/ucumUnits';
import { CategorizedSearchableDropdown } from './CategorizedSearchableDropdown';
import { DropDownItem } from './MultiSelectorDropdown/MultiSelectorDropDown';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export interface UnitOverrideSelectorProps {
    value?: string;
    onChange: (unit: string | undefined) => void;
    label?: string;
    className?: string;
    currentBackendUnit?: string; // The unit provided by the backend
}

/**
 * Shared component for unit override selection
 * Provides a dropdown to select UCUM units with a clear button
 */
export function UnitOverrideSelector({ 
    value, 
    onChange,
    label = "Unit Override",
    className = "",
    currentBackendUnit
}: UnitOverrideSelectorProps) {
    return (
        <div className={`flex items-center gap-4 ${className}`}>
            <Label className="text-textmedium min-w-[100px] flex items-center gap-1">
                {label}
                {currentBackendUnit && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <InfoIcon className="h-3 w-3 text-textdark" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Backend unit: {currentBackendUnit || 'none'}</p>
                            <p className="text-xs text-textdark mt-1">
                                Override this to display values in a different unit
                            </p>
                        </TooltipContent>
                    </Tooltip>
                )}
            </Label>
            <div className="flex-1 flex gap-2 items-center">
                <CategorizedSearchableDropdown
                    categories={UCUM_CATEGORIES}
                    searchPlaceholder="Search units..."
                    noMatchString="No unit found."
                    selected={(() => {
                        if (value) {
                            // Find the matching unit in UCUM_CATEGORIES
                            for (const category of UCUM_CATEGORIES) {
                                const item = category.items.find(item => item.value === value);
                                if (item) {
                                    return item;
                                }
                            }
                            // If not found, create a custom item
                            return { value: value, displayName: value };
                        }
                        return { value: "", displayName: "(Use backend unit)" };
                    })()}
                    setSelected={(item: DropDownItem) => {
                        onChange(item.value || undefined);
                    }}
                    allowAnyString={true}
                />
                {value && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onChange(undefined)}
                    >
                        <XIcon className="h-4 w-4"/>
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * Validates if a unit string is a valid UCUM unit
 * @param unit The unit string to validate
 * @returns true if the unit is valid, false otherwise
 */
export function isValidUcumUnit(unit: string): boolean {
    if (!unit || unit.trim() === '') {
        return true; // Empty unit is valid (unitless)
    }
    
    return UCUM_CATEGORIES.some(cat => 
        cat.items.some(item => item.value === unit)
    );
}